// src/services/auditService.js
// ─────────────────────────────────────────────────────────────────────────────
// Audit EVENTS service — owns the "auditevents" collection.
// History rows ("auditeventhistory") live in ./auditHistoryService.
// All event keys are aligned with AuditEventModel.ALLOWED_KEYS.
// ─────────────────────────────────────────────────────────────────────────────
import { repo } from "../dbservice/repo";
import { fetchAuditHistoryForEvents } from "./auditHistoryService";

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

export const ASSET_STATUS_KEYS = {
  pending: {
    key: "pending", label: "Pending",
    color: "#6b7280", bgColor: "#f9fafb", borderColor: "#e5e7eb",
    chipColor: "default",
  },
  verified: {
    key: "verified", label: "Verified",
    color: "#15803d", bgColor: "#f0fdf4", borderColor: "#bbf7d0",
    chipColor: "success",
  },
  missing: {
    key: "missing", label: "Missing",
    color: "#b91c1c", bgColor: "#fff1f2", borderColor: "#fecaca",
    chipColor: "error",
  },
  shifted: {
    key: "shifted", label: "Shifted",
    color: "#d97706", bgColor: "#fffbeb", borderColor: "#fde68a",
    chipColor: "warning",
  },
};

export const EVENT_STATUS_KEYS = {
  initiated: {
    key: "initiated", label: "Initiated", color: "primary",
    muiColor: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe",
  },
  in_progress: {
    key: "in_progress", label: "In Progress", color: "warning",
    muiColor: "#92400e", bg: "#fffbeb", border: "#fde68a",
  },
  completed: {
    key: "completed", label: "Completed", color: "success",
    muiColor: "#15803d", bg: "#f0fdf4", border: "#bbf7d0",
  },
};

const COL_EVENTS = "auditevents";

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function _getLocalClient() {
  try {
    if (!repo._hasValidLocalConfig()) return null;
    return repo._getLocalSupabaseClient();
  } catch {
    return null;
  }
}

const _log = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log("[auditService]", ...args);
  }
};

const _warn = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn("[auditService]", ...args);
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOCATIONS  (read-only lookups — not part of any audit collection)
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchLocations(companyId) {
  if (!companyId) throw new Error("fetchLocations: companyId is required");

  _log("fetchLocations", companyId);
  const client = _getLocalClient();

  if (client) {
    const { data, error } = await client
      .from("locations")
      .select("id, locationName, locationCode, name, details")
      .eq("companyId", companyId)
      .order("locationName", { ascending: true });

    if (error) throw error;
    return (data ?? []).map(_normalizeLocation);
  }

  const rows = await repo._fetchDataNative(
    "locations",
    [{ field: "companyId", value: companyId }],
    [],
    null
  );

  return rows.map(_normalizeLocation);
}

function _normalizeLocation(r) {
  const locationName =
    r.locationName          ??
    r.details?.locationName ??
    r.name                  ??
    r.details?.name         ??
    "";

  const locationCode =
    r.locationCode          ??
    r.details?.locationCode ??
    "";

  return {
    id:           r.id,
    name:         locationName,
    locationName,
    locationCode,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDITORS  (read-only lookups — not part of any audit collection)
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchAuditors(companyId) {
  if (!companyId) throw new Error("fetchAuditors: companyId is required");

  _log("fetchAuditors", companyId);
  const client = _getLocalClient();

  if (client) {
    const { data, error } = await client
      .from("users")
      .select("id, name, email, role")
      .eq("companyId", companyId)
      .eq("role", "auditor");

    if (error) throw error;
    return (data ?? []).map(_normalizeUser);
  }

  const rows = await repo._fetchDataNative(
    "users",
    [
      { field: "companyId", value: companyId },
      { field: "role",      value: "auditor" },
    ],
    [],
    null
  );

  return rows.map(_normalizeUser);
}

function _normalizeUser(r) {
  return {
    id:    r.id,
    name:  r.name  ?? "",
    email: r.email ?? "",
    role:  r.role  ?? "auditor",
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// ASSETS BY LOCATION  (read-only lookups — not part of any audit collection)
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchAssetsByLocation(
  locationId,
  companyId,
  locationCode = ""
) {
  if (!locationId) throw new Error("fetchAssetsByLocation: locationId required");
  if (!companyId)  throw new Error("fetchAssetsByLocation: companyId required");

  _log("fetchAssetsByLocation", { locationId, locationCode, companyId });

  const client = _getLocalClient();

  if (client) {
    const queries = [];

    if (locationId) {
      queries.push(
        client
          .from("assets")
          .select("id, details, assetName, assetTag, category, brand, model, locationId, locationCode")
          .eq("companyId", companyId)
          .eq("locationId", locationId)
      );
    }

    if (locationCode) {
      queries.push(
        client
          .from("assets")
          .select("id, details, assetName, assetTag, category, brand, model, locationId, locationCode")
          .eq("companyId", companyId)
          .eq("locationCode", locationCode)
      );
    }

    const results = await Promise.allSettled(queries);
    const seen    = new Set();
    const merged  = [];

    for (const result of results) {
      if (result.status !== "fulfilled") continue;
      const { data, error } = result.value;
      if (error) { _warn("Supabase asset query error:", error.message); continue; }

      for (const row of data ?? []) {
        if (!seen.has(row.id)) {
          seen.add(row.id);
          merged.push(row);
        }
      }
    }

    if (merged.length > 0) return merged.map(_normalizeAsset);

    const { data: jsonbData, error: jsonbError } = await client
      .from("assets")
      .select("id, details")
      .eq("companyId", companyId)
      .filter("details->>locationId", "eq", locationId);

    if (jsonbError) _warn("JSONB fallback error:", jsonbError.message);
    return (jsonbData ?? []).map(_normalizeAsset);
  }

  const all = await repo._fetchDataNative(
    "assets",
    [{ field: "companyId", value: companyId }],
    [],
    null
  );

  _log("fetchAssetsByLocation Firebase total:", all.length);

  const filtered = all.filter((r) => {
    const assetLocCode =
      r.locationCode          ??
      r.details?.locationCode ??
      "";

    const assetLocId =
      r.locationId          ??
      r.location_id         ??
      r.details?.locationId ??
      null;

    if (locationCode && assetLocCode && assetLocCode === locationCode) return true;
    if (locationId   && assetLocId   && assetLocId   === locationId)   return true;

    return false;
  });

  _log(
    `fetchAssetsByLocation matched ${filtered.length} of ${all.length}`,
    { locationCode, locationId }
  );

  return filtered.map(_normalizeAsset);
}

function _normalizeAsset(r) {
  const d = r.details ?? {};
  return {
    id:        r.id,
    assetName: r.assetName    ?? d.assetName    ?? r.name         ?? d.name         ?? "",
    assetTag:  r.assetId      ?? d.assetTag      ?? r.assetTag     ?? d.assetId      ?? "",
    category:  r.categoryName ?? d.category      ?? r.category     ?? d.categoryName ?? "",
    brand:     r.brandName    ?? d.brand         ?? r.brand        ?? d.brandName    ?? "",
    model:     r.model        ?? d.model         ?? "",
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIT EVENTS — FETCH ACTIVE
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchAuditEvents(companyId) {
  if (!companyId) throw new Error("fetchAuditEvents: companyId is required");

  const client = _getLocalClient();

  if (client) {
    const { data: events, error } = await client
      .from(COL_EVENTS)
      .select("*")
      .eq("companyId", companyId)
      .not("metadata->>deletedFlag", "eq", "true")
      .order("metadata->>createdAt", { ascending: false });

    if (error) throw error;
    if (!events?.length) return [];

    return _enrichEventsWithProgress(events, client);
  }

  _log("fetchAuditEvents Firebase", companyId);

  const events = await _fetchEventsByCompany(companyId);
  const active = events.filter((e) => e.metadata?.deletedFlag !== true);

  return _enrichEventsWithProgress(active, null);
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIT EVENTS — FETCH ARCHIVED
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchArchivedAuditEvents(companyId) {
  if (!companyId) throw new Error("fetchArchivedAuditEvents: companyId required");

  const client = _getLocalClient();

  if (client) {
    const { data: events, error } = await client
      .from(COL_EVENTS)
      .select("*")
      .eq("companyId", companyId)
      .filter("metadata->>deletedFlag", "eq", "true")
      .order("metadata->>updatedAt", { ascending: false });

    if (error) throw error;
    if (!events?.length) return [];

    return _enrichEventsWithProgress(events, client);
  }

  _log("fetchArchivedAuditEvents Firebase", companyId);

  const events   = await _fetchEventsByCompany(companyId);
  const archived = events.filter((e) => e.metadata?.deletedFlag === true);

  archived.sort((a, b) => {
    const at = new Date(a.metadata?.updatedAt ?? 0).getTime();
    const bt = new Date(b.metadata?.updatedAt ?? 0).getTime();
    return bt - at;
  });

  return _enrichEventsWithProgress(archived, null);
}

async function _fetchEventsByCompany(companyId) {
  const rows = await repo._fetchDataNative(
    COL_EVENTS,
    [{ field: "companyId", value: companyId }],
    [],
    null
  );

  return rows ?? [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// ENRICH EVENTS WITH PROGRESS
// ═══════════════════════════════════════════════════════════════════════════════

async function _enrichEventsWithProgress(events, client) {
  if (!events.length) return [];

  const eventIds       = events.map((e) => e.id);
  const historyByEvent = await fetchAuditHistoryForEvents(eventIds, client);

  return events.map((event) =>
    _buildEnrichedEvent(event, historyByEvent.get(event.id) ?? [])
  );
}

function _buildEnrichedEvent(event, history) {
  const total    = history.length;
  const verified = history.filter((h) => h.status === "verified").length;
  const missing  = history.filter((h) => h.status === "missing").length;
  const shifted  = history.filter((h) => h.status === "shifted").length;
  const done     = verified + missing + shifted;
  const pending  = total - done;
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  const remarks = history
    .map((h) => h.remark ?? h.notes ?? "")
    .filter(Boolean);

  let status = event.status ?? "initiated";

  if (status !== "completed" && status !== "in_progress") {
    if (done > 0) status = "in_progress";
    else          status = "initiated";
  }

  return {
    ...event,
    status,
    totalAssets:     total,
    verifiedAssets:  verified,
    missingAssets:   missing,
    shiftedAssets:   shifted,
    pendingAssets:   pending,
    doneAssets:      done,
    progress,
    remarks,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIT EVENTS — CREATE
// ═══════════════════════════════════════════════════════════════════════════════

export async function createAuditEvent(payload, assets = [], userId = "") {
  const coId = payload.companyId || payload.company_id;
  if (!coId) {
    throw new Error("createAuditEvent: companyId is required");
  }
  if (!Array.isArray(assets) || assets.length === 0) {
    throw new Error("createAuditEvent: at least one asset is required");
  }

  const client = _getLocalClient();
  const now    = new Date().toISOString();

  const eventPayload = {
    id:             payload.id || undefined,
    title:          payload.title          || "",
    description:    payload.description    || "",
    companyId:      coId,
    locationCode:   payload.locationCode || payload.location_code || "",
    auditor_id:     payload.auditor_id     || null,
    auditor_email:  payload.auditor_email  || null,
    scheduled_date: payload.scheduled_date || null,
    status:         "initiated",
    metadata: {
      createdAt:   now,
      createdBy:   userId || "system",
      updatedAt:   now,
      updatedBy:   userId || "system",
      deletedFlag: false,
    },
  };

  if (client) {
    const { data: newEvent, error: eventError } = await client
      .from(COL_EVENTS)
      .insert([eventPayload])
      .select()
      .single();

    if (eventError) throw eventError;

    const { seedAuditHistory } = await import("./auditHistoryService");
    try {
      await seedAuditHistory({
        auditEventId: newEvent.id,
        assets,
        companyId:    coId,
        locationCode: eventPayload.locationCode,
        createdBy:    userId,
      });
    } catch (histError) {
      await client.from(COL_EVENTS).delete().eq("id", newEvent.id);
      throw histError;
    }

    return newEvent;
  }

  const eventId = `audit_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  await repo._upsertDataCloud(COL_EVENTS, eventId, { ...eventPayload, id: eventId });

  const { seedAuditHistory } = await import("./auditHistoryService");
  await seedAuditHistory({
    auditEventId: eventId,
    assets,
    companyId:    coId,
    locationCode: eventPayload.locationCode,
    createdBy:    userId,
  });

  return { id: eventId, ...eventPayload };
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIT EVENTS — UPDATE
// ═══════════════════════════════════════════════════════════════════════════════

export async function updateAuditEvent(
  eventId,
  updates,
  assets = null,
  userId = ""
) {
  if (!eventId) throw new Error("updateAuditEvent: eventId is required");

  const client = _getLocalClient();
  const now    = new Date().toISOString();

  let currentEvent = null;

  if (client) {
    const { data, error } = await client
      .from(COL_EVENTS)
      .select("status, auditor_id, companyId, locationCode, metadata")
      .eq("id", eventId)
      .single();

    if (error) throw error;
    currentEvent = data;
  } else {
    const rows = await repo._fetchDataNative(
      COL_EVENTS,
      [{ field: "id", value: eventId }],
      [],
      null
    );
    currentEvent = rows?.[0] ?? null;
  }

  const auditIsLocked = currentEvent?.status === "completed";

  const auditorIsChanging =
    updates.auditor_id !== undefined &&
    updates.auditor_id !== (currentEvent?.auditor_id ?? "");

  if (auditIsLocked && auditorIsChanging) {
    throw new Error(
      "Cannot change the auditor once the audit is completed. " +
      "The audit has been finalized."
    );
  }

  const safeUpdates = { ...updates };

  if (auditIsLocked) {
    delete safeUpdates.auditor_id;
    delete safeUpdates.auditor_email;
  }

  // Build merged metadata (preserve existing, overlay updates, stamp updatedBy/At)
  const existingMeta = currentEvent?.metadata || {};
  const mergedMetadata = {
    createdAt:   existingMeta.createdAt   ?? null,
    createdBy:   existingMeta.createdBy   ?? null,
    updatedAt:   now,
    updatedBy:   userId || existingMeta.updatedBy || "system",
    deletedFlag: existingMeta.deletedFlag ?? false,
    ...(safeUpdates.metadata || {}),
  };

  // Strip metadata from flat updates so we don't double-write
  delete safeUpdates.metadata;

  const payload = {
    ...safeUpdates,
    metadata: mergedMetadata,
  };

  if (client) {
    const { data: updatedEvent, error: eventError } = await client
      .from(COL_EVENTS)
      .update(payload)
      .eq("id", eventId)
      .select()
      .single();

    if (eventError) throw eventError;

    if (Array.isArray(assets)) {
      const { replaceAuditHistory } = await import("./auditHistoryService");
      const coId         = payload.companyId  ?? updatedEvent.companyId  ?? "";
      const locationCode = payload.locationCode ?? updatedEvent.locationCode ?? "";

      await replaceAuditHistory({
        auditEventId: eventId,
        assets,
        companyId:    coId,
        locationCode,
        createdBy:    userId,
        client,
      });
    }

    return updatedEvent;
  }

  await repo._upsertDataCloud(COL_EVENTS, eventId, { ...payload, id: eventId });

  if (Array.isArray(assets)) {
    const { replaceAuditHistory } = await import("./auditHistoryService");
    const coId         = payload.companyId  ?? currentEvent?.companyId  ?? "";
    const locationCode = payload.locationCode ?? currentEvent?.locationCode ?? "";

    await replaceAuditHistory({
      auditEventId: eventId,
      assets,
      companyId:    coId,
      locationCode,
      createdBy:    userId,
      client:       null,
    });
  }

  return { id: eventId, ...payload };
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIT EVENTS — SOFT DELETE
// ═══════════════════════════════════════════════════════════════════════════════

export async function softDeleteAuditEvent(eventId, deletedByUserId = "") {
  if (!eventId) throw new Error("softDeleteAuditEvent: eventId is required");

  const client = _getLocalClient();
  const now    = new Date().toISOString();

  if (client) {
    // Fetch existing metadata so we can merge (Supabase replaces JSONB wholesale)
    const { data: existing, error: fetchErr } = await client
      .from(COL_EVENTS)
      .select("metadata")
      .eq("id", eventId)
      .single();

    if (fetchErr) throw fetchErr;

    const existingMeta = existing?.metadata || {};
    const updatedMeta = {
      ...existingMeta,
      deletedFlag: true,
      updatedAt:   now,
      updatedBy:   deletedByUserId || existingMeta.updatedBy || "system",
    };

    const { error } = await client
      .from(COL_EVENTS)
      .update({ metadata: updatedMeta })
      .eq("id", eventId);

    if (error) throw error;
  } else {
    const existing = await repo._fetchDataNative(
      COL_EVENTS,
      [{ field: "id", value: eventId }],
      [],
      null
    );

    const base         = existing?.[0] ?? {};
    const existingMeta = base.metadata || {};

    await repo._upsertDataCloud(COL_EVENTS, eventId, {
      ...base,
      metadata: {
        ...existingMeta,
        deletedFlag: true,
        updatedAt:   now,
        updatedBy:   deletedByUserId || existingMeta.updatedBy || "system",
      },
      id: eventId,
    });
  }

  _log("softDeleteAuditEvent", eventId, "by", deletedByUserId);
  return { success: true, id: eventId };
}

export const deleteAuditEvent = softDeleteAuditEvent;

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIT EVENTS — RESTORE
// ═══════════════════════════════════════════════════════════════════════════════

export async function restoreAuditEvent(eventId, restoredByUserId = "") {
  if (!eventId) throw new Error("restoreAuditEvent: eventId is required");

  const client = _getLocalClient();
  const now    = new Date().toISOString();

  if (client) {
    const { data: existing, error: fetchErr } = await client
      .from(COL_EVENTS)
      .select("metadata")
      .eq("id", eventId)
      .single();

    if (fetchErr) throw fetchErr;

    const existingMeta = existing?.metadata || {};
    const updatedMeta = {
      ...existingMeta,
      deletedFlag: false,
      updatedAt:   now,
      updatedBy:   restoredByUserId || existingMeta.updatedBy || "system",
    };

    const { error } = await client
      .from(COL_EVENTS)
      .update({ metadata: updatedMeta })
      .eq("id", eventId);

    if (error) throw error;
  } else {
    const existing = await repo._fetchDataNative(
      COL_EVENTS,
      [{ field: "id", value: eventId }],
      [],
      null
    );

    const base         = existing?.[0] ?? {};
    const existingMeta = base.metadata || {};

    await repo._upsertDataCloud(COL_EVENTS, eventId, {
      ...base,
      metadata: {
        ...existingMeta,
        deletedFlag: false,
        updatedAt:   now,
        updatedBy:   restoredByUserId || existingMeta.updatedBy || "system",
      },
      id: eventId,
    });
  }

  _log("restoreAuditEvent", eventId, "by", restoredByUserId);
  return { success: true, id: eventId };
}